import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import Checkbox from "@mui/material/Checkbox";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import AddTablesDialog from "./AddTablesDialog";
import DeleteTablesDialog from "./DeleteTablesDialog";
import RestoreTablesDialog from "./RestoreTablesDialog";
import ChangeTableQrDialog from "./ChangeTableQrDialog";

import { getTablesList } from "../../../../services/Admin/TablesApi";
import { useAuth } from "../../../../Context/AuthContext";


const TablesPage = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const visibleTables = tables.filter((t) => t.status);
  const hiddenTables = tables.filter((t) => !t.status);
  const { hasPermission } = useAuth();

  const fetchTables = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getTablesList();
      setTables(res?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load tables");
    } finally {
      setLoading(false);
    }
  };

  const [openAdd, setOpenAdd] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const rows = tabIndex === 0 ? visibleTables : hiddenTables;

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const [openDelete, setOpenDelete] = useState(false);

  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);
  const [openRestore, setOpenRestore] = useState(false);

  const handleOpenRestore = () => setOpenRestore(true);
  const handleCloseRestore = () => setOpenRestore(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openChangeQr, setOpenChangeQr] = useState(false);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const exists = prev.includes(id);
      if (exists) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const handleToggleSelectAll = () => {
    if (!rows || rows.length === 0) return;
    const allSelected = selectedIds.length === rows.length;
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(rows.map((t) => t.id));
  };

  const handleOpenChangeQr = () => setOpenChangeQr(true);
  const handleCloseChangeQr = () => setOpenChangeQr(false);

  const handleTabChange = (e, v) => {
    setTabIndex(v);
    setSelectedIds([]);
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchTables();
    return () => (mounted = false);
  }, []);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );

  if (error) return <Box sx={{ color: "error.main", p: 2 }}>{String(error)}</Box>;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>الطاولات</Typography>
        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 1 }}>
          <Tab label="الطاولات" />
          <Tab label="الطاولات المحذوفة" />
        </Tabs>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 1 }}>
          {tabIndex === 0 && hasPermission("delete") && (
            
            <Button sx={{ mr: 1 }} variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleOpenDelete}>
              حذف طاولات
            </Button>
          )}
          {tabIndex === 1 && hasPermission("edit") && (
            <Button sx={{ mr: 1 }} variant="outlined" startIcon={<RestoreIcon />} onClick={handleOpenRestore}>
              استرجاع طاولات
            </Button>
          )}
                       {hasPermission("add") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            إضافة طاولات
          </Button>
            )}
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selectedIds.length > 0 && selectedIds.length < (rows?.length || 0)}
                  checked={rows?.length > 0 && selectedIds.length === (rows?.length || 0)}
                  onChange={handleToggleSelectAll}
                />
              </TableCell>
              <TableCell>الاسم</TableCell>
              <TableCell>متاح</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>تاريخ الإنشاء</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(t.id)}
                        onChange={() => handleToggleSelect(t.id)}
                      />
                    </TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.available ? "نعم" : "لا"}</TableCell>
                    <TableCell>{t.status ? "مفعل" : "محذوف"}</TableCell>
                    <TableCell>{t.created_at ? new Date(t.created_at).toLocaleString("ar-EG", { hour12: true }) : "-"}</TableCell>
                  </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

        <AddTablesDialog
          open={openAdd}
          onClose={handleCloseAdd}
          onAdded={() => {
            // refresh list after successful creation
            fetchTables();
          }}
        />

        <DeleteTablesDialog
          open={openDelete}
          onClose={handleCloseDelete}
          onHidden={() => {
            fetchTables();
          }}
        />

        <RestoreTablesDialog
          open={openRestore}
          onClose={handleCloseRestore}
          onRestored={() => {
            fetchTables();
          }}
        />

        {/* <ChangeTableQrDialog
          open={openChangeQr}
          onClose={handleCloseChangeQr}
          initialFrom={selectedIds[0]}
          initialTo={selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : undefined}
          initialFromName={tables.find((x) => x.id === selectedIds[0])?.name}
          initialToName={tables.find((x) => x.id === (selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : undefined))?.name}
          onChanged={() => {
            fetchTables();
            setSelectedIds([]);
          }}
        /> */}
    </Paper>
  );
};

export default TablesPage;
